import { blinkConstants } from '@thoughtspot/blink-constants';
import { AppModule, useEmbedContext } from '@thoughtspot/blink-context';
import { getIsNavigationV3Enabled } from '@thoughtspot/feature-service';
import {
    Content,
    ContentWidth,
    Layout,
    LeftSideBar,
} from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import cx from 'classnames';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { NavHubContextProvider } from '/@contexts/nav-hub-context';
import { isFullAppEmbed } from '/@utils/embed.util';
import { LeftNavigationHub } from '../components/nav/navigation/left-navigation-hub.container';
import { InitSessionCheckContainer } from '../components/session-status-check/init-session-check.container';
import { ClientStateContextProvider } from '../contexts/global-client-state';
import { setTopNavInternalVariables } from '../theme/top-nav-v3/top-nav-v3-theme-service';
import { isLeftPanelVisible } from './authenticated-app.util';
import styles from './authenticated-app-layout.module.scss';
import {
    getAppModule,
    getIsOverlayLeftPanel,
    useIsInsightsPage,
} from './routes-util';

export type AuthenticatedAppLayoutProps = {
    isGlobalNavHidden: boolean;
    isPageContentFullHeight: boolean;
};

/**
 * There are three different left navigation modes
 * When isNavigationV3Enabled flag not enabled,
 * 1. Default mode mode, where the left navigation and right content will load together
 * When isNavigationV3Enabled flag enabled, there are two modes
 * 2. Hub mode, where the left navigation will be loaded separately from the navigation mfe
 * 3. Overlay mode, where the left navigation will be loaded separately as an overlay window
 */
export const AuthenticatedAppLayout: React.FC<AuthenticatedAppLayoutProps> = ({
    isGlobalNavHidden,
    isPageContentFullHeight,
}) => {
    const { sessionService, customStylingService } = useAppContext();
    const embedConfig = useEmbedContext();
    const { isEmbedded } = embedConfig;

    const isNavigationV3Enabled = getIsNavigationV3Enabled(embedConfig);

    const isInsightsPage = useIsInsightsPage();
    const appModule = getAppModule();
    const isOverlayPanel = getIsOverlayLeftPanel(isNavigationV3Enabled);
    const hideNavigationHub =
        appModule === AppModule.Admin || appModule === AppModule.OnBoarding;
    const themeType = customStylingService.getConfig()?.getThemeType();
    const config = customStylingService.getConfig();
    const leftNavThemeType = useMemo(() => {
        if (themeType === blinkConstants.themeType.CUSTOM && config) {
            const appPanelColor = config.getAppPanelColor(isNavigationV3Enabled)
                ?.color;
            if (appPanelColor) {
                const normalizedAppPanelColor = appPanelColor
                    .toUpperCase()
                    .substring(0, 7);
                const normalizedDarkColor = blinkConstants.themeColor.DARK.toUpperCase().substring(
                    0,
                    7,
                );

                if (normalizedAppPanelColor === normalizedDarkColor) {
                    return blinkConstants.themeType.DARK;
                }
            }
        }
        return themeType;
    }, [themeType, config, isNavigationV3Enabled]);

    useEffect(() => {
        if (isNavigationV3Enabled) {
            // Set the selected theme to the root element for navigation v3.
            document
                .getElementById('root')
                ?.setAttribute('data-theme-type', leftNavThemeType);

            // Set the top nav v3 internal variables from the style customization.
            if (themeType === blinkConstants.themeType.CUSTOM) {
                setTopNavInternalVariables(
                    customStylingService.getConfig()?.getAppPanelColor().color,
                );
            }
        } else {
            document.getElementById('root')?.removeAttribute('data-theme-type');
        }
    }, [
        isNavigationV3Enabled,
        themeType,
        leftNavThemeType,
        customStylingService,
    ]);

    if (
        isNavigationV3Enabled &&
        !hideNavigationHub &&
        !isPageContentFullHeight &&
        (!isEmbedded || isFullAppEmbed())
    ) {
        return (
            <NavHubContextProvider>
                <Layout isAppLayout>
                    <LeftSideBar
                        className={cx(styles.appLayoutLeftSideBar, {
                            [styles.borderRight]: isLeftPanelVisible(
                                isOverlayPanel,
                                isGlobalNavHidden,
                                isNavigationV3Enabled,
                                isInsightsPage,
                                sessionService,
                            ),
                            [styles.noBorderRight]: !isLeftPanelVisible(
                                isOverlayPanel,
                                isGlobalNavHidden,
                                isNavigationV3Enabled,
                                isInsightsPage,
                                sessionService,
                            ),
                        })}
                        isLeftPanelExpanded={isLeftPanelVisible(
                            isOverlayPanel,
                            isGlobalNavHidden,
                            isNavigationV3Enabled,
                            isInsightsPage,
                            sessionService,
                        )}
                        isLeftPanelFloating={false}
                        leftPanelMinWidth={260}
                    >
                        <LeftNavigationHub themeType={leftNavThemeType} />
                    </LeftSideBar>
                    <Content
                        contentWidth={ContentWidth.FLUID}
                        noPadding
                        disableOverlayScroll
                        className={styles.content}
                    >
                        <Suspense
                            fallback={
                                <LoadingIndicator.Global
                                    className={styles.loadingIndicator}
                                >
                                    <></>
                                </LoadingIndicator.Global>
                            }
                        >
                            <ClientStateContextProvider>
                                <Suspense
                                    fallback={
                                        <LoadingIndicator.Global
                                            className={styles.loadingIndicator}
                                        >
                                            <></>
                                        </LoadingIndicator.Global>
                                    }
                                >
                                    <Outlet />
                                    {sessionService.isLoggedIn() && (
                                        <InitSessionCheckContainer />
                                    )}
                                </Suspense>
                            </ClientStateContextProvider>
                        </Suspense>
                    </Content>
                </Layout>
            </NavHubContextProvider>
        );
    }

    return (
        <Suspense
            fallback={
                <LoadingIndicator.Global className={styles.loadingIndicator}>
                    <></>
                </LoadingIndicator.Global>
            }
        >
            <ClientStateContextProvider>
                <Suspense
                    fallback={
                        <LoadingIndicator.Global
                            className={styles.loadingIndicator}
                        >
                            <></>
                        </LoadingIndicator.Global>
                    }
                >
                    <Outlet />
                    {sessionService.isLoggedIn() && (
                        <InitSessionCheckContainer />
                    )}
                </Suspense>
            </ClientStateContextProvider>
        </Suspense>
    );
};
